import Cta, { CtaStyle, CtaType } from './index';
import calculateRate from '../util/calculate-rate';
import getCtaType from '../util/cta-type';
import configuration from '../configuration';
import commonCss from './css/common.css';
import { createLinkTag, createStyleTag, CssVariables, StyleTag } from '../util/create-style-tag';
import AssetWrapper from '../asset/wrappers';
import { StartPrequal } from '../events';
import { lowestNumber } from '../util/parse-data';
import { infoButtonComponent, bannerSvgComponent, calculatorSvgComponent } from '../util/svg-component';
import OlToolTip from './tooltip';
import Asset from '../asset/asset';
import { logToPartnerCtaClick } from '../util/logging';
import {
  ACCESSIBLE_COLORS,
  contrastRatio,
  resolveBackgroundColor,
  resolveNonTextColor,
  resolveTextColor
} from '../util/color-contrast';

type CtaPalette = {
  buttonBackgroundColor: string;
  buttonTextColor: string;
  buttonBoundaryColor: string;
  buttonAccentColor: string;
  miniCtaButtonMsrpTextColor: string;
};

export default abstract class GenericCta<AssetType extends Asset> extends Cta<AssetType> {
  public showMonthlyPayment = configuration.show_monthly_payment;
  public styleTag?: StyleTag = undefined;
  tooltip?: OlToolTip<AssetType>;

  constructor(
    wrapper: AssetWrapper<AssetType>,
    options?: {
      showMonthlyPayment?: boolean;
      style?: CtaStyle;
      styleTag?: StyleTag;
    }
  ) {
    super(wrapper);
    this.style = options?.style !== undefined ? options.style : this.style;
    if (
      configuration.flow_type === 'lead_first' &&
      configuration.enable_estimate_payment_step &&
      (this.style === 'standard' || this.style === 'slim')
    ) {
      this.style = 'estimate-payment';
    }
    if (options?.showMonthlyPayment !== undefined) {
      this.showMonthlyPayment = this.showMonthlyPayment && options.showMonthlyPayment;
    }

    // option created to allow style the CTA button
    this.styleTag = options?.styleTag;

    // Show the Prequal modal when the CTA is clicked
    this.onClick((event: Event) => {
      const target = event.target as HTMLElement;
      const clickedButton = target?.closest('.ol-button');
      const buttonType = clickedButton?.getAttribute('data-button-type');

      if (buttonType === 'estimate-payment') {
        configuration.show_estimate_payment_step = true;
      } else {
        configuration.show_estimate_payment_step = false;
      }

      if (['banner', 'sidebar'].includes(this.style)) {
        configuration.enable_vehicle_selection_step = true;
      } else {
        configuration.enable_vehicle_selection_step = false;
      }

      const ctaType: CtaType = getCtaType(this.wrapper.platform.pageType, this.style);
      configuration.cta_type = ctaType;
      void logToPartnerCtaClick(this.wrapper.platform.pageType, ctaType);

      wrapper.platform.eventBus.dispatchEvent(
        new StartPrequal({
          detail: {
            asset: wrapper.asset,
            cta_type: ctaType,
            invoker: event.currentTarget instanceof HTMLElement ? event.currentTarget : clickedButton || undefined
          }
        })
      );
    });
  }

  protected configuredButtonBackground(): string {
    if (this.style === 'sidebar') return configuration.sidebar_cta_button_color;
    if (this.wrapper.platform.pageType === 'homepage') return configuration.homepage_cta_button_color;
    if (['srp', 'srp_legacy'].includes(this.wrapper.platform.pageType)) return configuration.srp_cta_button_color;
    return configuration.cta_button_color;
  }

  protected resolveCtaPalette(): CtaPalette {
    const buttonBackgroundColor = resolveBackgroundColor(this.configuredButtonBackground(), '#3BB9FF');
    return {
      buttonBackgroundColor,
      buttonTextColor: resolveTextColor(configuration.cta_button_text_color, buttonBackgroundColor, '#000000'),
      buttonBoundaryColor:
        contrastRatio(buttonBackgroundColor, ACCESSIBLE_COLORS.white) >= 3
          ? buttonBackgroundColor
          : ACCESSIBLE_COLORS.boundary,
      buttonAccentColor: resolveNonTextColor(buttonBackgroundColor, ACCESSIBLE_COLORS.white, ACCESSIBLE_COLORS.accent),
      miniCtaButtonMsrpTextColor: resolveTextColor(
        configuration.minicta_button_msrp_text_color,
        buttonBackgroundColor,
        '#000000'
      )
    };
  }

  returnCssVariables(): CssVariables {
    const palette = this.resolveCtaPalette();
    return {
      ...palette,
      secondaryTextColor: ACCESSIBLE_COLORS.secondaryText,
      componentBorderColor: ACCESSIBLE_COLORS.boundary,
      summaryAccentColor: ACCESSIBLE_COLORS.summaryAccent,
      tooltipBackgroundColor: '#ffffff',
      tooltipTextColor: ACCESSIBLE_COLORS.secondaryText,
      mobileBreakpoint: '992px'
    };
  }

  returnCtaStylesTags(): StyleTag[] {
    const cssTags = [createStyleTag(commonCss.toString(), 'cta-base-style')];
    if (this.styleTag) cssTags.push(this.styleTag);
    return cssTags;
  }

  returnDocumentStyleTags(): StyleTag[] {
    return [createLinkTag('https://fonts.googleapis.com/css?family=Karla:400,700|Nunito:400,700')];
  }

  protected createElement<K extends keyof HTMLElementTagNameMap>(
    tagName: K,
    classNames: string[] = [],
    text?: string
  ): HTMLElementTagNameMap[K] {
    const element = document.createElement(tagName);
    element.classList.add(...classNames);
    if (text !== undefined) element.textContent = text;
    return element;
  }

  protected buildPaymentContainer(asset: AssetType, rate: number): HTMLElement {
    const msrpContainer = this.createElement('div', ['ol-msrp-container', 'ol-msrp-container-search']);
    if (asset.price && asset.price > 0) {
      msrpContainer.setAttribute('originalPrice', String(asset.originalPrice));
      msrpContainer.setAttribute('price', String(asset.price));
    }

    const rateContainer = this.createElement('div', ['ol-rate-container-search']);
    rateContainer.appendChild(this.createElement('p', ['ol-body'], 'from'));

    const tooltipLink = this.createElement('div', ['ol-tooltip-link']);
    tooltipLink.appendChild(this.createElement('p', ['ol-title', 'ol-msrp-search'], `$${rate}`));
    tooltipLink.appendChild(this.createElement('p', ['ol-body', 'ol-month-label'], '/ mo.'));
    tooltipLink.appendChild(infoButtonComponent());
    rateContainer.appendChild(tooltipLink);

    msrpContainer.appendChild(rateContainer);
    msrpContainer.appendChild(
      this.createElement('p', ['ol-body', 'ol-msrp-subtitle-search'], 'Instant \u2022 No credit impact')
    );
    return msrpContainer;
  }

  buildGenericCtaElement(asset: AssetType, rate: number): HTMLElement {
    // CTA Container
    const cta = document.createElement('div');
    // Add generic CTA classes
    cta.classList.add('ol-cta', 'ol-cta-search');
    if (this.style === 'slim') cta.classList.add('ol-cta-slim');
    else if (this.style === 'standard') cta.classList.add('ol-cta-standard');
    else throw new Error(`CTA type "${this.style}" not expected.`);

    // Prepare buttonContainer
    const buttonContainer = document.createElement('div');
    buttonContainer.classList.add('ol-button-container-search');
    const button = this.createElement('button', ['ol-button', 'ol-button-search']);
    button.type = 'button';
    button.dataset.linksInitialized = 'false';
    button.appendChild(this.createElement('span', ['ol-title', 'ol-button-text-search'], configuration.cta_text));
    button.addEventListener('click', this.handleOnClick.bind(this));
    buttonContainer.appendChild(button);

    cta.appendChild(this.buildPaymentContainer(asset, rate));
    // Standard No Payment button is before info text
    if (!(this.showMonthlyPayment && rate > 0) && this.style === 'standard') cta.prepend(buttonContainer);
    // Otherwise, regular button placement is after info text
    else cta.append(buttonContainer);
    return cta;
  }

  buildBannerCtaElement(): HTMLElement {
    // CTA Container
    const cta = document.createElement('div');
    // Add generic CTA classes
    cta.classList.add('ol-cta-search', 'ol-cta-banner');
    const ctaColor = this.resolveCtaPalette().buttonAccentColor;
    cta.style.borderColor = ctaColor;

    const bannerContainer = document.createElement('div');
    bannerContainer.classList.add('ol-container-banner');

    // Prepare buttonContainer
    const buttonContainer = document.createElement('div');
    buttonContainer.classList.add('ol-button-container-banner');
    const button = this.createElement('button', ['ol-button', 'ol-button-banner']);
    button.type = 'button';
    button.dataset.linksInitialized = 'false';
    button.appendChild(this.createElement('span', ['ol-banner-button-title'], 'Get Started'));
    button.addEventListener('click', this.handleOnClick.bind(this));
    buttonContainer.appendChild(button);

    const bannerIcon = this.createElement('div', ['ol-banner-icon']);
    bannerIcon.appendChild(
      bannerSvgComponent({
        banner_cta_button_color: ctaColor
      })
    );

    const bannerText = this.createElement('div', ['ol-banner-text']);
    const titleContainer = document.createElement('div');
    titleContainer.appendChild(this.createElement('p', ['ol-banner-title'], 'What can I afford?'));
    const bodyContainer = document.createElement('div');
    bodyContainer.appendChild(
      this.createElement('p', ['ol-banner-body'], 'Build your deal and understand your spending power')
    );
    bannerText.append(titleContainer, bodyContainer);
    bannerContainer.append(bannerIcon, bannerText);
    cta.append(bannerContainer);
    cta.append(buttonContainer);
    return cta;
  }

  buildEstimatePaymentCtaElement(asset: AssetType, rate: number): HTMLElement {
    // CTA Container
    const cta = document.createElement('div');
    // Add generic CTA classes
    cta.classList.add('ol-cta', 'ol-cta-search', 'has-estimate-payment');

    // Prepare buttonContainer
    const buttonContainer = document.createElement('div');
    buttonContainer.classList.add('ol-button-container-search');
    const getPrequalifiedButton = this.createElement('button', ['ol-button', 'ol-button-search-estimate-payment']);
    getPrequalifiedButton.type = 'button';
    getPrequalifiedButton.style.marginBottom = '8px';
    getPrequalifiedButton.dataset.linksInitialized = 'false';
    getPrequalifiedButton.dataset.buttonType = 'get-prequalified';
    getPrequalifiedButton.appendChild(
      this.createElement('span', ['ol-title', 'ol-button-text-search'], configuration.cta_text)
    );
    getPrequalifiedButton.addEventListener('click', this.handleOnClick.bind(this));

    const estimatePaymentButton = this.createElement('button', ['ol-button', 'ol-button-search-estimate-payment']);
    estimatePaymentButton.type = 'button';
    estimatePaymentButton.dataset.linksInitialized = 'false';
    estimatePaymentButton.dataset.buttonType = 'estimate-payment';
    estimatePaymentButton.appendChild(
      this.createElement('span', ['ol-title', 'ol-button-text-search'], configuration.estimate_payment_cta_text)
    );
    estimatePaymentButton.addEventListener('click', this.handleOnClick.bind(this));
    buttonContainer.append(getPrequalifiedButton, estimatePaymentButton);

    cta.appendChild(this.buildPaymentContainer(asset, rate));
    cta.append(buttonContainer);
    return cta;
  }

  buildMiniCtaElement(asset: AssetType, rate: number): HTMLElement {
    // CTA Container
    const cta = document.createElement('div');
    cta.classList.add('ol-cta', 'ol-cta-search', 'ol-cta-mini', 'ol-button');
    const actionButton = this.createElement('button', ['ol-button-text', 'ol-mini-cta-action']);
    actionButton.type = 'button';
    actionButton.appendChild(this.createElement('span', [], configuration.cta_text));
    actionButton.addEventListener('click', this.handleOnClick.bind(this));

    const msrpContainer = this.createElement('div', ['ol-msrp-container']);
    const msrpText = this.createElement('span', ['ol-cta-text-msrp'], `as low as $${rate}`);
    msrpText.appendChild(this.createElement('span', [], '/mo'));
    const tooltipButton = this.createElement('span', ['ol-cta-tooltip-button', 'ol-tooltip-link']);
    tooltipButton.appendChild(
      infoButtonComponent({
        infoButtonColor: this.resolveCtaPalette().miniCtaButtonMsrpTextColor
      })
    );
    msrpContainer.append(msrpText, tooltipButton);
    cta.append(actionButton, msrpContainer);
    return cta;
  }

  buildSidebarCtaElement(): HTMLElement {
    const cta = document.createElement('div');
    cta.classList.add('ol-cta-sidebar');

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.classList.add('sidebar-close');
    closeButton.textContent = '\u00d7';
    closeButton.setAttribute('aria-label', 'Collapse Build My Payment');

    const iconAndLabel = document.createElement('button');
    iconAndLabel.type = 'button';
    iconAndLabel.classList.add('sidebar-header');
    iconAndLabel.setAttribute('aria-expanded', 'true');
    iconAndLabel.setAttribute('aria-label', 'Collapse Build My Payment');

    const icon = calculatorSvgComponent();
    icon.classList.add('sidebar-icon');

    const label = document.createElement('span');
    label.textContent = 'Build My Payment';
    label.classList.add('sidebar-label');

    iconAndLabel.appendChild(icon);
    iconAndLabel.appendChild(label);

    const setCollapsed = (collapsed: boolean) => {
      cta.classList.toggle('collapsed', collapsed);
      iconAndLabel.setAttribute('aria-expanded', String(!collapsed));
      iconAndLabel.setAttribute('aria-label', `${collapsed ? 'Expand' : 'Collapse'} Build My Payment`);
    };

    closeButton.addEventListener('click', () => setCollapsed(true));
    iconAndLabel.addEventListener('click', () => setCollapsed(!cta.classList.contains('collapsed')));

    cta.appendChild(closeButton);

    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = 'Get Started';
    button.classList.add('sidebar-button');
    button.addEventListener('click', this.handleOnClick.bind(this));

    cta.appendChild(iconAndLabel);
    cta.appendChild(button);
    return cta;
  }

  buildCtaElement(asset: AssetType, rate: number): HTMLElement {
    if (this.style === 'standard' || this.style === 'slim') {
      return this.buildGenericCtaElement(asset, rate);
    } else if (this.style === 'mini') {
      return this.buildMiniCtaElement(asset, rate);
    } else if (this.style === 'estimate-payment') {
      return this.buildEstimatePaymentCtaElement(asset, rate);
    } else if (this.style === 'banner') {
      return this.buildBannerCtaElement();
    } else if (this.style === 'sidebar') {
      return this.buildSidebarCtaElement();
    } else throw new Error(`CTA type "${this.style}" not expected.`);
  }

  generateHTML() {
    const asset: AssetType = this.wrapper.asset;
    // low price may be zero, but it lets us send a number instead of null to all our functions like calcRate
    const lowestPrice: number = lowestNumber(...asset.prices);
    const showMonthlyPayment = this.showMonthlyPayment && lowestPrice > 0;

    const rate = calculateRate(
      lowestPrice,
      configuration.doc_fee,
      configuration.apr_percent,
      configuration.term_months
    );

    const cta = this.buildCtaElement(asset, rate);

    if (!showMonthlyPayment) {
      cta.classList.add('ol-no-payment');
    }

    this.tooltip?.destroy();
    this.tooltip = undefined;
    const tooltipElement = cta.querySelector<HTMLElement>('.ol-info-button');
    if (tooltipElement) {
      this.tooltip = new OlToolTip<AssetType>(this.wrapper.asset, this, tooltipElement, lowestPrice);
    }
    return cta;
  }

  protected afterRender() {
    if (this.tooltip) this.shadowRoot.appendChild(this.tooltip.component);
  }
}
